import { FC, useEffect, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { trpc, InvoiceStatus } from 'src/trpc';
import { useSearchParams } from 'react-router-dom';
import { Loader } from 'components/Loader';
import { useDebounce } from 'components/Debounce';
import { InvoiceDataTable } from './InvoiceDataTable';
import { useOrganization } from 'src/context/OrganizationContext';

export const InvoiceDashboard: FC<{ userId?: string }> = ({ userId }) => {
  const [params, setParams] = useSearchParams({
    offset: '0',
    sortDirection: 'asc',
    sortKey: '',
    search: '',
    status: 'all',
  });

  // Use organization context for org-dependent queries
  const { organizationId, isResolved, isOrganizationSwitching, setIsOrganizationSwitching } =
    useOrganization();

  const offset = parseInt(params.get('offset')!);
  const limit = 25;
  let queryStatus = params.get('status') as InvoiceStatus | 'all' | undefined;
  if (queryStatus === 'all') {
    queryStatus = undefined;
  }
  const { data, isPending, isError, isFetching } = trpc.admin.invoice.adminListInvoices.useQuery(
    {
      limit,
      offset: params.get('offset') ? parseInt(params.get('offset')!) : 0,
      sortDirection: params.get('sortDirection') as 'asc' | 'desc',
      sortKey: params.get('sortKey')!,
      search: params.get('search') || '',
      status: queryStatus,
      userId: userId,
    },
    {
      placeholderData: keepPreviousData,
      enabled: isResolved && !!organizationId,
    },
  );

  // Reset organization switching state when fetch completes
  useEffect(() => {
    if (isOrganizationSwitching && !isFetching) {
      setIsOrganizationSwitching(false);
    }
  }, [isOrganizationSwitching, isFetching, setIsOrganizationSwitching]);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<InvoiceStatus | ''>(
    (params.get('status') as InvoiceStatus | '') || '',
  );
  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    params.set('search', debouncedSearch || '');
    params.set('offset', '0');
    setParams(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  useEffect(() => {
    params.set('status', status || '');
    params.set('offset', '0');
    setParams(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  if (!isResolved || isOrganizationSwitching || (isPending && isFetching)) {
    return <Loader title="Getting Invoices" />;
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <h2 className="text-xl font-semibold text-red-800">Error loading invoices</h2>
        <p className="mt-2 text-gray-600">
          Please try again later or contact support if the issue persists.
        </p>
      </div>
    );
  }

  return (
    <InvoiceDataTable
      invoiceData={data}
      setParams={(newParams) => {
        params.set('offset', newParams.offset?.toString() || '0');
        params.set('limit', newParams.limit?.toString() || '10');
        params.set('sortDirection', newParams.sortDirection || 'asc');
        params.set('sortKey', newParams.sortKey || '');
        setParams(params);
      }}
      pagination={{ offset, limit }}
      sortingObj={{
        sortDirection: params.get('sortDirection') as 'asc' | 'desc',
        sortKey: params.get('sortKey')!,
      }}
      search={search}
      setSearch={setSearch}
      status={status}
      setStatus={setStatus}
    />
  );
};
